import type { ReceiptFull, ReceiptState } from '@pflegenavi/shared/api';
import { Gender, ReceiptStatusValues } from '@pflegenavi/shared/api';
import type { ReceiptPhoenix } from './api';

/**
 * Converts a Phoenix receipt response to the legacy ReceiptFull format
 * This enables gradual migration from the NestJS to Phoenix backend
 */
export function convertPhoenixReceiptToReceiptFull(
  phoenixReceipt: ReceiptPhoenix
): ReceiptFull {
  // Convert receipt state string to enum
  const receiptStateMap: Record<string, ReceiptState> = {
    Submitted: 'Submitted' as ReceiptState,
    Pending: 'Pending' as ReceiptState,
    Draft: 'Draft' as ReceiptState,
    Incomplete: 'Incomplete' as ReceiptState,
    AwaitingSettlement: 'AwaitingSettlement' as ReceiptState,
    Settled: 'Settled' as ReceiptState,
  };

  const statusMap: Record<string, ReceiptStatusValues> = {
    Submitted: ReceiptStatusValues.SUBMITTED,
    Pending: ReceiptStatusValues.PENDING,
    Draft: ReceiptStatusValues.DRAFT,
    Incomplete: ReceiptStatusValues.INCOMPLETE,
    AwaitingSettlement: ReceiptStatusValues.AWAITING_SETTLEMENT,
    Settled: ReceiptStatusValues.SETTLED,
  };

  const genderMap: Record<string, Gender> = {
    Female: Gender.Female,
    Male: Gender.Male,
    Other: Gender.Other,
  };

  return {
    id: phoenixReceipt.id,
    amount: phoenixReceipt.amount / 100, // Phoenix stores in cents
    amountInCent: phoenixReceipt.amount,
    additionalFeeAmountInCent: phoenixReceipt.additional_fee_amount,
    includedFeeAmountInCent: phoenixReceipt.included_fee_amount,
    additionalFeesDivertedToPflegenavi:
      phoenixReceipt.additional_fees_diverted_to_pflegenavi,
    date: phoenixReceipt.receipt_date,
    resident: phoenixReceipt.resident
      ? {
          id: phoenixReceipt.resident.id,
          firstName: phoenixReceipt.resident.first_name,
          lastName: phoenixReceipt.resident.last_name,
          gender: genderMap[phoenixReceipt.resident.gender] || Gender.Other,
        }
      : {
          id: '',
          firstName: '',
          lastName: '',
          gender: Gender.Other,
        },
    receipt_not_available: phoenixReceipt.receipt_not_available,
    receipt_type: phoenixReceipt.receipt_type
      ? {
          id: phoenixReceipt.receipt_type.id,
          name: phoenixReceipt.receipt_type.name,
          requiresServiceProvider:
            phoenixReceipt.receipt_type.requiresServiceProvider,
        }
      : undefined,
    service_provider: phoenixReceipt.service_provider
      ? {
          id: phoenixReceipt.service_provider.id,
          name: phoenixReceipt.service_provider.name,
        }
      : undefined,
    creator:
      phoenixReceipt.created_by_employee &&
      phoenixReceipt.created_by_employee.first_name
        ? {
            firstName: phoenixReceipt.created_by_employee.first_name,
            lastName: phoenixReceipt.created_by_employee.last_name,
          }
        : undefined,
    status:
      statusMap[phoenixReceipt.receipt_state] || ReceiptStatusValues.DRAFT,
    receipt_state:
      receiptStateMap[phoenixReceipt.receipt_state] ||
      ('Draft' as ReceiptState),
    notes: phoenixReceipt.notes || undefined,
    receiptImageIds: phoenixReceipt.receipt_image_ids || [],
    created_on: phoenixReceipt.inserted_at,
    cancellation: phoenixReceipt.cancellation
      ? {
          transactionId: phoenixReceipt.cancellation.transaction_id,
          cancelTransactionId:
            phoenixReceipt.cancellation.cancel_transaction_id,
          employee: phoenixReceipt.cancellation.employee
            ? {
                firstName: phoenixReceipt.cancellation.employee.first_name,
                lastName: phoenixReceipt.cancellation.employee.last_name,
              }
            : {
                firstName: '',
                lastName: '',
              },
          date: phoenixReceipt.cancellation.date,
          note: phoenixReceipt.cancellation.note,
        }
      : undefined,
    submissionDate: phoenixReceipt.submission_date || undefined,
    embargoDate: phoenixReceipt.embargo_date || undefined,
    receiptBatch: phoenixReceipt.receipt_batch_entry
      ? {
          id: phoenixReceipt.receipt_batch_entry.receipt_batch_id,
          owner: phoenixReceipt.receipt_batch_entry.owner ?? undefined,
          title: '', // Not available in Phoenix response, would need to be fetched separately
          receiptImageIds: [], // Based on search results, this field is not used in the mobile app
        }
      : undefined,
    created_by_employee_id: phoenixReceipt.created_by_employee?.id,
    // Settlement fields
    settlement_amount: phoenixReceipt.settlement_amount,
    settlement_date: phoenixReceipt.settlement_date,
    settlement_notes: phoenixReceipt.settlement_notes ?? undefined,
    settled_by_employee: phoenixReceipt.settled_by_employee
      ? {
          firstName: phoenixReceipt.settled_by_employee.first_name,
          lastName: phoenixReceipt.settled_by_employee.last_name,
        }
      : null,
    settlement_cash_list_transaction_group_link:
      phoenixReceipt.settlement_cash_list_transaction_group_link,
    // Handle optional fields - these are only included when specifically requested
    linkedTransactionGroups: phoenixReceipt.cash_list_transaction_group_link
      ? [
          phoenixReceipt.cash_list_transaction_group_link
            .cash_list_transaction_group_id,
        ]
      : undefined,
    earnings: phoenixReceipt.earnings,
  };
}
